import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import history from 'service/customHistory';
import { useConditionalEffect } from 'react-delta';
import { useMutation } from '@apollo/client';

import ELEMENT from 'common/element';
import * as K from 'common/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import { UrlParams } from 'common/types';
import { buildUrl } from 'common/util/location';

import PromptDialog from 'components/common/Dialog/PromptDialog';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertConfig, PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { showWaitCursor } from 'components/common/Page/Page';
import { CREATE_DASHBOARD_V2, GET_CONFIGV2, LIST_DASHBOARD_NAMES } from 'components/dashboards/queries';

export enum AFTER_CREATE {
  ALERT = 'alert',
  GOTO = 'goto',
  NOTHING = 'nothing',
}

type DashboardCreatorProps = {
  showDialog?: boolean;
  dialogClose?: () => void;
  afterCreate?: AFTER_CREATE;
  newDashboard?: string;
};

/**
 * This component is a dashboard creation utility that can:
 * - Prompt for a new dashboard
 * - Verify the new dashboard name doesn't have any invalid chars (showing errors if needed)
 * - Create a new dashboard (with or without the dialog)
 * - If successful, it can redirect to the new dashboard, just show a prompt or do nothing
 *
 * Please use it when creating any new dashboard in the webapp to ensure that the navbar, dashboard list
 * page, etc., GraphQL caches are updated.
 *
 * @prop showDialog    true to show the dialog
 * @prop dialogClose   callback for hiding the dialog
 * @prop afterCreate   what to do after the dialog is created (see AFTER_CREATE enum, above)
 * @prop newDashboard  new dashboard name -- just set this directly to skip the dialog and validation
 */

function DashboardCreator({
  showDialog = false,
  dialogClose = (): void => {},
  afterCreate = AFTER_CREATE.ALERT,
  newDashboard = '',
}: DashboardCreatorProps): JSX.Element {
  const [dashboardName, setDashboardName] = useState(newDashboard);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [creatingDashboard, setCreatingDashboard] = useState(false);
  const [createDashboard] = useMutation(CREATE_DASHBOARD_V2, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName } }, { query: LIST_DASHBOARD_NAMES }],
  });

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const closeDialog = (): void => {
    setDashboardName('');
    setNewDashboardName('');
    setCreatingDashboard(false);
    dialogClose();
  };

  const errorCheckRegEx = new RegExp(/^[\w\d\s./_-]+$/);
  const checkForInvalidChars = (value: string): string | null => {
    if (value && !errorCheckRegEx.test(value)) {
      return 'Sorry, only letters, numbers, spaces and /_-. allowed';
    }
    return null;
  };

  const createNewDashboard = (): void => {
    if (dashboardName) {
      setCreatingDashboard(true);
      createDashboard({
        variables: { dashboardName },
      })
        .then(() => {
          if (afterCreate === AFTER_CREATE.ALERT) {
            setPageAlertConfig({
              message: `Dashboard ${dashboardName} created |`,
              variant: PageAlertVariant.INFO,
              linkHref: buildUrl(K.ROUTE.DASHBOARD, { [K.URL_PARAM.DASHBOARD_NAME]: dashboardName } as UrlParams),
              linkText: 'Go to dashboard',
              autoHide: true,
              duration: 5,
            });
          } else if (afterCreate === AFTER_CREATE.GOTO) {
            showWaitCursor();
            history.push(buildUrl(K.ROUTE.DASHBOARD, { [K.URL_PARAM.DASHBOARD_NAME]: dashboardName } as UrlParams));
          }
        })
        .catch((newError) => {
          const config: PageAlertConfig = {
            message: clearGraphQLErrorMessage(newError.message),
            autoHide: true,
          };
          if (newError.message.includes('already a dashboard named')) {
            config.message += ' |';
            config.linkHref = buildUrl(K.ROUTE.DASHBOARD, { [K.URL_PARAM.DASHBOARD_NAME]: dashboardName } as UrlParams);
            config.linkText = 'Go to it';
            config.duration = 10;
          } else {
            config.variant = PageAlertVariant.ERROR;
          }
          setPageAlertConfig(config);
        });
    }
  };

  useConditionalEffect(() => {
    createNewDashboard();
  }, dashboardName !== '' && !creatingDashboard);

  return (
    <>
      {showDialog && (
        <PromptDialog
          title="New Dashboard"
          label="Dashboard Name"
          inline={false}
          show={showDialog}
          close={closeDialog}
          value={newDashboardName}
          onChange={(ev): void => setNewDashboardName((ev.target as HTMLInputElement).value)}
          onSubmit={(): void => setDashboardName(newDashboardName)}
          getError={checkForInvalidChars}
          testId={ELEMENT.newDashboardDialog}
        />
      )}
    </>
  );
}

export default DashboardCreator;
